import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {HttpClient} from '@angular/common/http';

import {NbAccordionModule, NbAlertModule, NbCardModule, NbDatepickerModule, NbSpinnerModule, NbThemeModule} from '@nebular/theme';
import {
  NbDialogModule,
  NbMenuModule,
  NbSidebarModule,
  NbToastrModule,
  NbWindowModule,
  NbLayoutModule
} from '@nebular/theme';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {CarouselModule} from 'ngx-bootstrap/carousel';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {NbPasswordAuthStrategy, NbAuthModule, NbAuthJWTToken, NB_AUTH_TOKEN_INTERCEPTOR_FILTER} from '@nebular/auth';

import {environment} from '#env';
import {AuthGuard} from './guards/auth.guard';
import {HomeGuard} from './guards/home.guard';
import {TokenInterceptor} from './services/http.interceptor';
import {DataService} from './services/data.service';
import {HomeComponent} from './home/home.component';
import {UtilService} from './services/util.service';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {EditorModule} from '@tinymce/tinymce-angular';
import {SbCommonModule} from '@app/common.module';
import {SentryErrorHandler} from '@app/services/sentry.service';
import {HelpComponent} from './help/help.component';
import {HelpArticleComponent} from './help/help-article/help-article.component';
import {AccessDeniedComponent} from './access-denied/access-denied.component';
import {Router} from '@angular/router';
import * as Sentry from '@sentry/angular';
import {Integrations} from '@sentry/tracing';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

import {ServiceWorkerModule} from '@angular/service-worker';

Sentry.init({
  dsn: environment.sentry.url,
  environment: environment.environment,
  // TryCatch has to be configured to disable XMLHttpRequest wrapping, as we are going to handle
  // http module exceptions manually in Angular's ErrorHandler and we don't want it to capture the same error twice.
  // Please note that TryCatch configuration requires at least @sentry/browser v5.16.0.
  integrations: [
    new Sentry.Integrations.TryCatch({
      XMLHttpRequest: false
    }),
    new Integrations.BrowserTracing({
      tracingOrigins: ['localhost', environment.apiUrl],
      routingInstrumentation: Sentry.routingInstrumentation,
    })
  ],
  tracesSampleRate: 1.0
});

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    HelpComponent,
    HelpArticleComponent,
    AccessDeniedComponent
  ],
  imports: [
    SbCommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    NbThemeModule.forRoot({name: 'smartbite'}),
    AppRoutingModule,
    NgbModule,
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbWindowModule.forRoot(),
    NbAlertModule,
    NbSpinnerModule,
    HttpClientModule,
    NbDatepickerModule.forRoot(),
    CarouselModule.forRoot(),
    NbLayoutModule,
    FormsModule,
    EditorModule,
    ReactiveFormsModule,
    NbAccordionModule,
    NbAuthModule.forRoot({
      strategies: [
        NbPasswordAuthStrategy.setup({
          name: 'email',
          baseEndpoint: environment.apiUrl,
          login: {
            // ...
            endpoint: '/supplier-login',
            method: 'post',
            redirect: {
              success: '/pages/dashboard',
              failure: null // stay on the same page
            }
          },
          requestPass: {
            endpoint: '/supplier-request-password',
            method: 'post',
            redirect: {
              success: null,
              failure: null // stay on the same page
            }
          },
          resetPass: {
            endpoint: '/supplier-reset-password',
            method: 'post',
            redirect: {
              success: '/pages/dashboard',
              failure: null // stay on the same page
            }
          },
          token: {
            class: NbAuthJWTToken,
            key: 'token'
          },
          refreshToken: {
            endpoint: '/refresh-supplier-token',
            method: 'post',
            requireValidToken: false,
            redirect: {
              success: '/pages/dashboard',
              failure: null,
            },
          }
        })
      ],
      forms: {
        login: {
          redirectDelay: 0,
          strategy: 'email',
          rememberMe: true,   // whether to show or not the `rememberMe` checkbox
          showMessages: {     // show/not show success/error messages
            success: true,
            error: true
          }
        },
        resetPass: {
          redirectDelay: 0,
          strategy: 'email',
          showMessages: {     // show/not show success/error messages
            success: true,
            error: true
          }
        },
        logout: {
          redirectDelay: 0
        }
      }
    }),
    NbCardModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'en'
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {enabled: true})
  ],
  providers: [
    AuthGuard,
    HomeGuard,
    DataService,
    UtilService,
    {provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true},
    {
      provide: NB_AUTH_TOKEN_INTERCEPTOR_FILTER, useValue: (req) => {
        return false;
      }
    },
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
      },
      multi: true,
      deps: [Sentry.TraceService]
    }
  ],
  exports: [SbCommonModule],
  bootstrap: [AppComponent]
})
export class AppModule {
}
